import { z } from 'zod';

// ─── member medical information vocabularies ────────────────────────────────
//
// Governed enums for the append-only `member_medical_information_records` history: the
// allergies, past medical history, current medications and pregnancy status a pharmacy order needs.
// The `as const` arrays drive BOTH the `text`+CHECK constraints rendered in the
// DB schema and any wire schema (ADR-056 Rule 8). The row shape itself is NOT
// mirrored here: readers consume `typeof memberMedicalInformation.$inferSelect`
// (ADR-066).

/**
 * Which writer appended the row. `healthie`: parsed from a finalised Healthie
 * intake form, with `source_ref` carrying the form answer group id so a
 * redelivered webhook is idempotent. `consultation`: recorded during a
 * clinical consultation. `legacy_import`: copied from a Pelago 1.0 row, with
 * `source_ref` carrying the 1.0 id and `source_version` fixed to
 * `MEMBER_MEDICAL_INFORMATION_LEGACY_IMPORT_SOURCE_VERSION` for backfill idempotency.
 */
export const MEMBER_MEDICAL_INFORMATION_SOURCE = [
  'healthie',
  'consultation',
  'legacy_import',
] as const;
export const memberMedicalInformationSourceSchema = z.enum(MEMBER_MEDICAL_INFORMATION_SOURCE);
export type MemberMedicalInformationSource = z.infer<typeof memberMedicalInformationSourceSchema>;

/**
 * Fixed `source_version` for every `legacy_import` row so the partial unique on
 * `(source, source_ref, source_version)` dedupes a Tier 2 backfill re-run.
 */
export const MEMBER_MEDICAL_INFORMATION_LEGACY_IMPORT_SOURCE_VERSION = 'legacy' as const;

/**
 * Upper bound on every free-text column (the rendered allergy and medication
 * context, and `source_ref`): the zero-ETL replication ceiling for an
 * unbounded `text` (ADR-105 Rule 5).
 */
export const MEMBER_MEDICAL_INFORMATION_TEXT_MAX_LENGTH = 65535;
